import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Snackbar } from "@mui/material";
import { useConnectMatchListings } from "../hooks/useConnectMatchListings";
import { useConnectCurrentUser } from "../hooks/useConnectCurrentUser";


import { useMatches } from "../hooks/useMatches";
import { useMatchActions } from "../hooks/useMatch";
import { useUserId } from "../hooks/useUserId";

import { LandingPage } from "./LandingPage";
import { WaitingRoom } from "./WaitingRoom";
import { Match } from "./Match";
import {
  selectCurrentMatchId,
  selectCurrentMatch,
  selectPlayersForMatch,
  selectMyUserId,
  selectCurrentMatchGames,
  selectMe,
} from "../store/selectors";

type View = "landing" | "waiting" | "playing";

export const HomeNew: React.FC = () => {
  const userId = useSelector(selectMyUserId);
  const currentUser = useSelector(selectMe);
  const { logout } = useUserId();

  // Set up Firestore listeners for matches and current user
  useConnectMatchListings();
  useConnectCurrentUser({ userId: userId ?? '' });
  const currentMatchId = useSelector(selectCurrentMatchId);
  const currentMatch = useSelector(selectCurrentMatch);
  const currentMatchPlayers = useSelector(
    selectPlayersForMatch(currentMatchId ?? "")
  );
  const currentMatchGames = useSelector(selectCurrentMatchGames);

  const { createQuickBotMatch, createOnlineRoom, findMatchByCode, addBotToMatch, removeBotFromMatch } =
    useMatches();
  const { joinMatch, leaveMatch, startGameForMatch } = useMatchActions();

  const [error, setError] = useState<string | null>(null);

  // Determine current view
  const isInMatch = !!currentMatchId && !!currentMatch;
  const isWaitingRoom = isInMatch && (currentMatch as any).isWaitingRoom;
  // Only show playing state if there's at least one game
  const hasGames = currentMatchGames && currentMatchGames.length > 0;
  const isPlaying = isInMatch && !isWaitingRoom && hasGames;

  // Check if we're waiting for game creation (in a match with 4 players, but no games yet)
  const isWaitingForGame = isInMatch && !isWaitingRoom && !hasGames && (currentMatchPlayers?.length ?? 0) >= 4;

  const currentView: View = isPlaying
    ? "playing"
    : isWaitingRoom || isWaitingForGame
    ? "waiting"
    : "landing";

  // Handler for "Play vs Bots" - creates match and starts game immediately
  const handlePlayVsBots = useCallback(async () => {
    try {
      const { matchId, reservedHumanSeat, botPlayers } = await createQuickBotMatch();
      const humanPlayer = await joinMatch(matchId, reservedHumanSeat);
      // Start game immediately with all 4 players (3 bots + 1 human)
      const allPlayers = [...botPlayers, humanPlayer];
      await startGameForMatch(matchId, allPlayers);
    } catch (e) {
      console.error("Error creating/joining match:", e);
      setError("Failed to create game. Please try again.");
    }
  }, [createQuickBotMatch, joinMatch, startGameForMatch]);

  // Handler for "Create Room"
  const handleCreateRoom = useCallback(async () => {
    try {
      const { matchId } = await createOnlineRoom();
      await joinMatch(matchId);
    } catch (e) {
      setError("Failed to create room. Please try again.");
    }
  }, [createOnlineRoom, joinMatch]);

  // Handler for "Join Room"
  const handleJoinRoom = useCallback(
    async (code: string) => {
      const match = findMatchByCode(code);
      if (!match) {
        setError(`Room "${code}" not found. Check the code and try again.`);
        return;
      }
      try {
        await joinMatch(match.id);
      } catch (e) {
        setError("Failed to join room. Please try again.");
      }
    },
    [findMatchByCode, joinMatch]
  );

  // Handler for leaving a room
  const handleBack = useCallback(async () => {
    if (currentMatchId) {
      await leaveMatch(currentMatchId);
    }
  }, [currentMatchId, leaveMatch]);

  // Handler for adding a bot
  const handleAddBot = useCallback(async () => {
    if (currentMatchId) {
      await addBotToMatch(currentMatchId);
    }
  }, [currentMatchId, addBotToMatch]);

  // Handler for removing a bot
  const handleRemoveBot = useCallback(
    async (playerId: string) => {
      if (currentMatchId) {
        await removeBotFromMatch(currentMatchId, playerId);
      }
    },
    [currentMatchId, removeBotFromMatch]
  );

  // Handler for starting the game
  const handleStart = useCallback(async () => {
    if (currentMatchId && currentMatchPlayers) {
      await startGameForMatch(currentMatchId, currentMatchPlayers);
    }
  }, [currentMatchId, currentMatchPlayers, startGameForMatch]);

  // Check if current user is host (first human player to join)
  const isHost =
    currentMatchPlayers?.find((p) => p.human)?.id === userId;

  const canStart = (currentMatchPlayers?.length ?? 0) === 4;

  const roomCode = (currentMatch as any)?.roomCode ?? "------";

  return (
    <>
      {currentView === "landing" && (
        <LandingPage
          onPlayVsBots={handlePlayVsBots}
          onCreateRoom={handleCreateRoom}
          onJoinRoom={handleJoinRoom}
          onLogout={logout}
          userName={currentUser?.name}
        />
      )}

      {currentView === "waiting" && (
        <WaitingRoom
          roomCode={roomCode}
          players={currentMatchPlayers ?? []}
          isHost={isHost}
          canStart={canStart}
          onBack={handleBack}
          onAddBot={handleAddBot}
          onRemoveBot={handleRemoveBot}
          onStart={handleStart}
        />
      )}

      {currentView === "playing" && <Match />}

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
